import { Link, useNavigate } from 'react-router-dom';
import { FiTrash2, FiMinus, FiPlus, FiShoppingBag, FiArrowRight, FiTag } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import './CartPage.css';

const SHIPPING_THRESHOLD = 2000;
const SHIPPING_COST = 150;
const picsum = (seed) => `https://picsum.photos/seed/${seed}/80/80`;

export default function CartPage() {
  const { cartItems, updateQuantity, removeItem, cartTotal, cartCount } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  const shipping = cartTotal >= SHIPPING_THRESHOLD ? 0 : SHIPPING_COST;
  const grandTotal = cartTotal + shipping;

  const handleCheckout = () => {
    if (!user) return navigate('/login', { state: { from: { pathname: '/checkout' } } });
    navigate('/checkout');
  };

  if (cartCount === 0) {
    return (
      <div className="cart-page">
        <div className="container">
          <div className="cart-empty">
            <div className="cart-empty-icon">🛒</div>
            <h2>Your cart is empty</h2>
            <p>Looks like you haven't added anything yet. Explore our products and find something you love!</p>
            <Link to="/products" className="btn btn-primary btn-lg">
              <FiShoppingBag size={18} /> Browse Products
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="cart-page">
      <div className="container">
        <div className="cart-header">
          <h1>Your Cart</h1>
          <span className="cart-count-label">{cartCount} item{cartCount !== 1 ? 's' : ''}</span>
        </div>

        <div className="cart-layout">
          {/* Items */}
          <div className="cart-items-col">
            <AnimatePresence initial={false}>
              {cartItems.map((item) => {
                const price = item.salePrice ?? item.price;
                const imgSrc = item.images?.[0] || picsum(item._id);

                return (
                  <motion.div
                    key={item._id}
                    className="cart-item card"
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, x: -40, height: 0, marginBottom: 0, padding: 0 }}
                    transition={{ duration: 0.2 }}
                    layout
                  >
                    {/* Image */}
                    <Link to={`/products/${item._id}`} className="cart-item-img-wrap">
                      <img
                        src={imgSrc}
                        alt={item.name}
                        className="cart-item-img"
                        onError={(e) => { e.target.src = picsum(item.name); }}
                      />
                    </Link>

                    {/* Info */}
                    <div className="cart-item-info">
                      {item.vendor?.storeName && (
                        <span className="cart-item-vendor">{item.vendor.storeName}</span>
                      )}
                      <Link to={`/products/${item._id}`} className="cart-item-name">
                        {item.name}
                      </Link>
                      <div className="cart-item-price-row">
                        <span className="cart-item-price">Rs. {price.toFixed(2)}</span>
                        {item.salePrice && (
                          <span className="cart-item-orig">Rs. {item.price.toFixed(2)}</span>
                        )}
                      </div>
                    </div>

                    {/* Controls */}
                    <div className="cart-item-controls">
                      <div className="qty-stepper">
                        <button
                          className="qty-btn"
                          onClick={() => updateQuantity(item._id, item.quantity - 1)}
                          disabled={item.quantity <= 1}
                          aria-label="Decrease quantity"
                        >
                          <FiMinus size={13} />
                        </button>
                        <span className="qty-value">{item.quantity}</span>
                        <button
                          className="qty-btn"
                          onClick={() => updateQuantity(item._id, item.quantity + 1)}
                          disabled={item.quantity >= (item.stock ?? 99)}
                          aria-label="Increase quantity"
                        >
                          <FiPlus size={13} />
                        </button>
                      </div>
                      <span className="cart-item-line-total">
                        Rs. {(price * item.quantity).toFixed(2)}
                      </span>
                      <button
                        className="cart-remove-btn"
                        onClick={() => removeItem(item._id)}
                        aria-label="Remove item"
                      >
                        <FiTrash2 size={15} />
                      </button>
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>

            <Link to="/products" className="cart-continue-link">
              ← Continue Shopping
            </Link>
          </div>

          {/* Summary */}
          <aside className="cart-summary card">
            <div className="card-header">
              <h2 className="cart-summary-title">Order Summary</h2>
            </div>
            <div className="card-body cart-summary-body">
              <div className="summary-row">
                <span>Subtotal ({cartCount} item{cartCount !== 1 ? 's' : ''})</span>
                <span>Rs. {cartTotal.toFixed(2)}</span>
              </div>
              <div className="summary-row">
                <span className="flex items-center gap-2">
                  <FiTag size={13} /> Shipping
                </span>
                {shipping === 0 ? (
                  <span className="summary-free">FREE</span>
                ) : (
                  <span>Rs. {SHIPPING_COST.toFixed(2)}</span>
                )}
              </div>

              {shipping > 0 && (
                <div className="shipping-tip">
                  Add <strong>Rs. {(SHIPPING_THRESHOLD - cartTotal).toFixed(2)}</strong> more for free shipping
                  <div className="shipping-progress">
                    <div
                      className="shipping-bar"
                      style={{ width: `${Math.min(100, (cartTotal / SHIPPING_THRESHOLD) * 100)}%` }}
                    />
                  </div>
                </div>
              )}

              {shipping === 0 && (
                <div className="shipping-achieved">
                  🎉 You've unlocked free shipping!
                </div>
              )}

              <div className="summary-divider" />

              <div className="summary-row summary-total">
                <span>Total</span>
                <span>Rs. {grandTotal.toFixed(2)}</span>
              </div>

              <button
                className="btn btn-primary btn-lg btn-block checkout-btn"
                onClick={handleCheckout}
              >
                Proceed to Checkout <FiArrowRight size={16} />
              </button>

              <div className="secure-badges">
                <span>🔒 Secure Checkout</span>
                <span>·</span>
                <span>🛡 Buyer Protection</span>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
